import clsx from 'clsx';
import React from 'react';

import { TextAlignment } from '@gitbook/api-client';
import { FRAGMENT_CAPTION } from '@gitbook/doc-core';
import type { Block } from '@gitbook/slate';
import { type BlockProps, type EditorContainer, FragmentRenderer } from '@gitbook/slate-react';

import { BlockVariantContext } from '../contexts';
import { TextAlignmentContext } from '../text-alignment';

export interface CaptionState {
    selectCaption: () => void;
    removeCaption: () => void;
    hasCaption: boolean;
}

/**
 * Returns a function to show a caption. A small utility around Slate selection.
 */
export function useCaption(node: Block, editor: EditorContainer): CaptionState {
    const fragment = node.fragments.get(FRAGMENT_CAPTION);

    return React.useMemo(() => {
        if (!fragment) {
            return { selectCaption: () => {}, removeCaption: () => {}, hasCaption: false };
        }

        return {
            hasCaption: !fragment.isEmpty,
            removeCaption: () => {
                editor.change((change) => {
                    fragment.nodes.forEach((descendant) => {
                        change.removeNodeByKey(descendant.key);
                    });
                });
            },
            selectCaption: () => {
                editor.change((change) => {
                    change.collapseToStartOf(fragment).moveFocusToEndOf(fragment).focus();
                });
            },
        };
    }, [fragment, editor]);
}

/**
 * Render the caption fragment on a block.
 * The caption will be visible if it has content, of if it is focused.
 * If you want to show an empty caption, move the Slate focus to the fragment.
 */
export function EditableCaption(props: BlockProps & { className?: string }) {
    const { node, editor, className } = props;
    const caption = node.fragments.get(FRAGMENT_CAPTION);

    if (!caption) {
        return null;
    }

    const isFocused =
        editor.value.selection.hasAnchorIn(caption) || editor.value.selection.hasFocusIn(caption);

    if (caption.isEmpty && !isFocused) {
        return null;
    }

    return (
        <div
            className={clsx(
                'group/caption-editor relative mt-2 min-w-20 pt-1 text-center text-ui-small',
                className
            )}
        >
            {caption.isEmpty ? (
                <div className="absolute inset-0 w-full pt-1 text-muted">Write a caption</div>
            ) : null}
            <BlockVariantContext.Provider value="caption">
                <TextAlignmentContext.Provider value={TextAlignment.Center}>
                    <FragmentRenderer fragment="caption" />
                </TextAlignmentContext.Provider>
            </BlockVariantContext.Provider>
        </div>
    );
}
